<template>
  <div class="transactions-container">
    <!-- Page Header -->
    <div class="page-header">
      <h2 class="page-title">{{ $t('transactionsManagement.title') }}</h2>
      <button class="btn-primary" @click="showAddTransactionForm = true">
        <i class="fas fa-plus"></i> {{ $t('transactionsManagement.addTransaction') }}
      </button>
    </div>

    <!-- Search Bar -->
    <!-- Search Bar with User Suggestions -->
    <div class="search-container">
      <i class="fas fa-search search-icon"></i>
      <div class="search-input-wrapper">
        <input 
          type="text" 
          class="search-input" 
          :placeholder="$t('transactionsManagement.searchTransactions')" 
          v-model="searchQuery"
          @input="fetchSearchSuggestions"
          @focus="showSearchSuggestions = true"
          @keyup.enter="searchTransactions"
        />
        
        <!-- User Suggestions Dropdown -->
        <div class="search-suggestions" v-if="showSearchSuggestions && searchSuggestions.length > 0">
          <div 
            v-for="user in searchSuggestions" 
            :key="user.id" 
            class="suggestion-item"
            @click="selectSearchUser(user)"
          >
            <div class="suggestion-name">
              {{ user.first_name }} {{ user.last_name }}
              <span :class="['role-tag', `role-${user.role}`]">{{ user.role }}</span>
            </div>
            <div class="suggestion-details">
              <span class="suggestion-username">@{{ user.username }}</span>
              <span class="suggestion-balance">{{ formatCurrency(user.balance) }}</span>
            </div>
          </div>
        </div>
      </div>
      <button class="btn-search" @click="searchTransactions">
        {{ $t('transactionsManagement.search') }}
      </button>
    </div>

    <!-- Transaction Table -->
    <div class="table-responsive" v-if="transactions.length">
      <table class="transaction-table">
        <thead>
          <tr>
            <th @click="sortTransactions('id')">
              {{ $t('transactionsManagement.id') }}
              <span class="sort-indicator" :class="{'active': sortKey === 'id'}">
                <i class="fas" :class="sortKey === 'id' && sortOrder === 1 ? 'fa-sort-up' : sortKey === 'id' && sortOrder === -1 ? 'fa-sort-down' : 'fa-sort'"></i>
              </span>
            </th>
            <th @click="sortTransactions('user_name')">
              {{ $t('transactionsManagement.userName') }}
              <span class="sort-indicator" :class="{'active': sortKey === 'user_name'}">
                <i class="fas" :class="sortKey === 'user_name' && sortOrder === 1 ? 'fa-sort-up' : sortKey === 'user_name' && sortOrder === -1 ? 'fa-sort-down' : 'fa-sort'"></i>
              </span>
            </th>
            <th @click="sortTransactions('amount')">
              {{ $t('transactionsManagement.amount') }}
              <span class="sort-indicator" :class="{'active': sortKey === 'amount'}">
                <i class="fas" :class="sortKey === 'amount' && sortOrder === 1 ? 'fa-sort-up' : sortKey === 'amount' && sortOrder === -1 ? 'fa-sort-down' : 'fa-sort'"></i>
              </span>
            </th>
            <th @click="sortTransactions('balance_after')">
              {{ $t('transactionsManagement.balanceAfter') }}
              <span class="sort-indicator" :class="{'active': sortKey === 'balance_after'}">
                <i class="fas" :class="sortKey === 'balance_after' && sortOrder === 1 ? 'fa-sort-up' : sortKey === 'balance_after' && sortOrder === -1 ? 'fa-sort-down' : 'fa-sort'"></i>
              </span>
            </th>
            <th @click="sortTransactions('date')">
              {{ $t('transactionsManagement.date') }}
              <span class="sort-indicator" :class="{'active': sortKey === 'date'}">
                <i class="fas" :class="sortKey === 'date' && sortOrder === 1 ? 'fa-sort-up' : sortKey === 'date' && sortOrder === -1 ? 'fa-sort-down' : 'fa-sort'"></i>
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="transaction in transactions" :key="transaction.id">
            <td>{{ transaction.id }}</td>
            <td>{{ transaction.user_name }}</td>
            <td>
              <span :class="[
                'amount-cell', 
                parseFloat(transaction.amount) > 0 ? 'amount-positive' : 
                parseFloat(transaction.amount) < 0 ? 'amount-negative' : 'amount-zero'
              ]">
                {{ formatCurrency(transaction.amount) }}
              </span>
            </td>
            <td>
              <span :class="[
                'balance-cell', 
                parseFloat(transaction.balance_after) > 0 ? 'balance-positive' : 
                parseFloat(transaction.balance_after) < 0 ? 'balance-negative' : 'balance-zero'
              ]">
                {{ formatCurrency(transaction.balance_after) }}
              </span>
            </td>
            <td>{{ formatDate(transaction.date) }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Empty State -->
    <div class="empty-state" v-else>
      <i class="fas fa-receipt"></i>
      <p>{{ $t('transactionsManagement.noTransactionsFound') }}</p>
    </div>

    <!-- Pagination -->
    <div class="pagination-container">
      <div class="pagination-controls">
        <div class="per-page-selector">
          <label for="page-size">{{ $t('transactionsManagement.perPage') }}</label>
          <select id="page-size" v-model="pageSize" @change="handlePageSizeChange" class="page-size-select">
            <option value="5">5</option>
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        </div>
        
        <div class="page-info" v-if="totalTransactions > 0">
          {{ $t('transactionsManagement.showing') }} 
          {{ transactions.length === 0 ? 0 : (currentPage - 1) * pageSize + 1 }}-{{ Math.min(currentPage * pageSize, totalTransactions) }} 
          {{ $t('transactionsManagement.of') }} {{ totalTransactions }}
        </div>
      </div>
      
      <div class="pagination-nav">
        <button 
          @click="prevPage" 
          :disabled="currentPage === 1" 
          class="page-btn"
        >
          <i class="fas fa-chevron-left"></i> {{ $t('transactionsManagement.previous') }}
        </button>
        
        <span class="page-status">{{ $t('transactionsManagement.page') }} {{ currentPage }} / {{ totalPages }}</span>
        
        <button 
          @click="nextPage" 
          :disabled="currentPage >= totalPages" 
          class="page-btn"
        >
          {{ $t('transactionsManagement.next') }} <i class="fas fa-chevron-right"></i>
        </button>
      </div>
    </div>

    <!-- Add Transaction Form -->
    <div class="overlay" v-if="showAddTransactionForm">
      <div class="form-popup">
        <div class="form-header">
          <h2>{{ $t('transactionsManagement.addNewTransaction') }}</h2>
          <button class="btn-close" @click="closeAddTransactionForm">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <form @submit.prevent="addTransaction">
          <div class="form-group">
            <label class="form-label">{{ $t('transactionsManagement.user') }}</label>
            <div class="search-user-container">
              <input 
                type="text" 
                class="form-control" 
                v-model="transactionUserQuery" 
                @input="fetchTransactionUserSuggestions"
                @focus="showUserSuggestions = true"
                :placeholder="$t('transactionsManagement.searchUserPlaceholder')"
              >
              <div class="user-suggestions" v-if="showTransactionUserSuggestions && transactionUserSuggestions.length > 0">
                <div 
                  v-for="user in transactionUserSuggestions" 
                  :key="user.id" 
                  class="user-suggestion-item"
                  @click="selectTransactionUser(user)"
                >
                  <div class="user-name">
                    {{ user.first_name }} {{ user.last_name }}
                    <span :class="['user-role', `role-${user.role}`]">{{ user.role }}</span>
                  </div>
                  <div class="user-details">
                    <span class="user-username">@{{ user.username }}</span>
                    <span class="user-balance">{{ formatCurrency(user.balance) }}</span>
                  </div>
                </div>
              </div>
              <div class="selected-user" v-if="newTransaction.user_id && selectedUserName">
                <div class="selected-user-info">
                  <strong>{{ selectedUserName }}</strong>
                  <span class="selected-user-balance">{{ $t('transactionsManagement.currentBalance') }}: {{ formatCurrency(selectedUserBalance) }}</span>
                </div>
              </div>
            </div>
          </div>
          
          <div class="form-group">
            <label class="form-label">{{ $t('transactionsManagement.amount') }}</label>
            <div class="amount-input-container">
              <input 
                type="number" 
                class="form-control" 
                v-model.number="newTransaction.amount" 
                step="1"
                required
              >
              <div class="amount-hint">
                <small>{{ $t('transactionsManagement.amountHint') }}</small>
              </div>
            </div>
          </div>
          
          <div class="form-buttons">
            <button type="button" class="btn-secondary" @click="closeAddTransactionForm">
              {{ $t('transactionsManagement.cancel') }}
            </button>
            <button type="submit" class="btn-primary" :disabled="!newTransaction.user_id">
              {{ $t('transactionsManagement.addTransaction') }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Toast Notification -->
    <div class="toast-notification" v-if="notificationVisible">
      <i class="fas fa-check-circle"></i>
      <span class="toast-message">{{ notificationMessage }}</span>
    </div>
  </div>
</template>

<script>
import axiosInstance from '../router/axiosSetup';
export default {
  data() {
    return {
      searchQuery: '',
      transactions: [],
      showAddTransactionForm: false,
      newTransaction: {
        user_id: null,
        amount: 0,
      },
      transactionUserQuery: '',
      filteredUsers: [],
      sortKey: 'date',
      sortOrder: 1,
      currentPage: 1,
      pageSize: 10,
      searchSuggestions: [],
      showSearchSuggestions: false,
      transactionUserSuggestions: [],
      showTransactionUserSuggestions: false,
      selectedUserName: '',
      selectedUserBalance: 0
    };
  },
  methods: {
    async fetchSearchSuggestions() {
      if (this.searchQuery.length < 2) {
        this.searchSuggestions = [];
        return;
      }
      
      try {
        const response = await axiosInstance.get(`/admin/user-suggestions?query=${this.searchQuery}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        
        this.searchSuggestions = response.data;
        this.showSearchSuggestions = this.searchSuggestions.length > 0;
      } catch (error) {
        console.error('Error fetching search suggestions:', error);
        this.searchSuggestions = [];
      }
    },
    async fetchTransactionUserSuggestions() {
      if (this.transactionUserQuery.length < 2) {
        this.transactionUserSuggestions = [];
        return;
      }
      
      try {
        const response = await axiosInstance.get(`/admin/user-suggestions?query=${this.transactionUserQuery}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        
        this.transactionUserSuggestions = response.data;
        this.showTransactionUserSuggestions = this.transactionUserSuggestions.length > 0;
      } catch (error) {
        console.error('Error fetching transaction user suggestions:', error);
        this.transactionUserSuggestions = [];
      }
    },
    
    selectTransactionUser(user) {
      this.transactionUserQuery = `${user.first_name} ${user.last_name}`;
      this.showTransactionUserSuggestions = false;
      this.newTransaction.user_id = user.id;
      this.selectedUserName = `${user.first_name} ${user.last_name}`;
      this.selectedUserBalance = user.balance;
    },
    
    // Add to document click handler
    setupClickOutside() {
      document.addEventListener('click', this.handleClickOutside);
    },
    
    handleClickOutside(event) {
      const searchContainer = this.$el.querySelector('.search-container');
      const formContainer = this.$el.querySelector('.search-user-container');
      
      if (searchContainer && !searchContainer.contains(event.target)) {
        this.showSearchSuggestions = false;
      }
      
      if (formContainer && !formContainer.contains(event.target)) {
        this.showTransactionUserSuggestions = false;
      }
    },
    selectSearchUser(user) {
      this.searchQuery = `${user.first_name} ${user.last_name}`;
      this.showSearchSuggestions = false;
      this.searchTransactions();
    },
    handlePageSizeChange() {
      // Reset to first page when changing page size
      this.currentPage = 1;
      this.searchTransactions();
      
      // Store preference in localStorage
      localStorage.setItem('transactionsPageSize', this.pageSize);
    },
    sortTransactions(key) {
        if (this.sortKey === key) {
        this.sortOrder *= -1;
        } else {
        this.sortKey = key;
        this.sortOrder = 1;
        }
        this.searchTransactions(); // Fetch transactions again with the new sorting
    },
    async searchTransactions() {
      try {
        this.isLoading = true;
        const response = await axiosInstance.get(
          `/admin/search-transactions?query=${encodeURIComponent(this.searchQuery)}&limit=${this.pageSize}&offset=${(this.currentPage - 1) * this.pageSize}&sortKey=${this.sortKey}&sortOrder=${this.sortOrder === 1 ? 'ASC' : 'DESC'}`, 
          {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          }
        );
        
        this.transactions = response.data.transactions;
        this.totalTransactions = response.data.total || this.transactions.length;
        this.isLoading = false;
      } catch (error) {
        console.error('Error searching transactions:', error);
        this.isLoading = false;
        this.transactions = [];
        this.totalTransactions = 0;
      }
    },
    showNotification(message) {
      this.notificationMessage = message || this.$t('transactionsManagement.transactionAdded');
      this.notificationVisible = true;
      // Auto-hide after 3 seconds
      setTimeout(() => {
        this.notificationVisible = false;
      }, 3000);
    },
    async searchUsers() {
        try {
        const response = await axiosInstance.get(`/admin/search-users?query=${this.transactionUserQuery}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        this.filteredUsers = response.data;
        } catch (error) {
        console.error('Error searching users:', error);
        }
    },
    selectUser(user) {
        this.newTransaction.user_id = user.id;
        this.transactionUserQuery = `${user.first_name} ${user.last_name}`;
        this.filteredUsers = [];
    },
    async addTransaction() {
      try {
        await axiosInstance.post('/admin/add-transaction', this.newTransaction, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        this.showNotification(this.$t('transactionsManagement.transactionAdded'));
        this.searchTransactions(); // Refresh the transaction list
        this.resetNewTransaction();
        this.showAddTransactionForm = false;
      } catch (error) {
        console.error('Error adding transaction:', error);
      }
    },
    formatCurrency(value) {
      return new Intl.NumberFormat('uk-UA', {
        style: 'currency',
        currency: 'UAH',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value);
    },
    formatDate(dateString) {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat(this.$i18n.locale, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    },
    resetNewTransaction() {
        this.newTransaction = {
        user_id: null,
        amount: 0,
        };
        this.transactionUserQuery = '';
    },
    closeAddTransactionForm() {
        this.showAddTransactionForm = false; // Hide the add transaction form
        this.newTransaction = { user_id: null, amount: 0 };
        this.transactionUserQuery = '';
        this.selectedUserName = '';
        this.selectedUserBalance = 0;
    },
    nextPage() {
        this.currentPage++;
        this.searchTransactions();
    },
    prevPage() {
        if (this.currentPage > 1) {
        this.currentPage--;
        this.searchTransactions();
        }
    }
    },
  created() {
    const savedPageSize = localStorage.getItem('transactionsPageSize');
    if (savedPageSize) {
      this.pageSize = parseInt(savedPageSize);
    }
    
    // Initialize data object properties
    this.isLoading = false;
    this.totalTransactions = 0;
    this.notificationMessage = '';
    this.notificationVisible = false;
    this.showUserSuggestions = false;
    this.showTransactionUserSuggestions = false;
    this.searchTransactions(); // Populate transactions list on component creation
  },
  mounted() {
    this.setupClickOutside();
    // Other mounted code...
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
    // Other cleanup code...
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.totalTransactions / this.pageSize));
    }
  },
};
</script>

<style scoped>
/* Base Layout */
.transactions-container {
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
  color: #333;
}

/* Page Header */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.page-title {
  font-size: 1.75rem;
  font-weight: 600;
  color: #212529;
  margin: 0;
}

/* Search Container */
.search-container {
  position: relative;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  max-width: 800px;
  width: 100%;
}

.search-icon {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #64748b;
  font-size: 16px;
  pointer-events: none;
  z-index: 5;
}

.search-input {
  flex: 1;
  padding: 10px 12px 10px 40px;
  height: 42px;
  border-radius: 21px;
  border: 1px solid #e2e8f0;
  font-size: 0.95rem;
  transition: all 0.2s;
  width: 100%; 
}

.search-input:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.25);
  outline: none;
}

.btn-search {
  margin-left: 10px;
  padding: 8px 16px;
  background-color: #3b82f6;
  color: white;
  border: none;
  border-radius: 21px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-search:hover {
  background-color: #2563eb;
}

/* Table Styling */
.table-responsive {
  overflow-x: auto;
  margin-bottom: 24px;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}

.transaction-table {
  width: 100%;
  border-collapse: collapse;
  overflow: hidden;
}

.transaction-table th {
  padding: 16px 12px;
  font-weight: 600;
  color: #495057;
  border-bottom: 2px solid #dee2e6;
  background-color: #f8f9fa;
  text-align: left;
  cursor: pointer;
  user-select: none;
  transition: background-color 0.2s;
}

.transaction-table th:hover {
  background-color: #e9ecef;
}

.sort-indicator {
  margin-left: 8px;
  opacity: 0.5;
}

.sort-indicator.active {
  opacity: 1;
}

.transaction-table tbody tr {
  transition: background-color 0.2s;
  border-bottom: 1px solid #edf2f7;
}

.transaction-table tbody tr:hover {
  background-color: #f1f5f9;
}

.transaction-table td {
  padding: 14px 12px;
  vertical-align: middle;
}

/* Amount and Balance Styling */
.amount-cell, .balance-cell {
  font-family: 'Roboto Mono', monospace;
  font-weight: 500;
  padding: 4px 8px;
  border-radius: 4px;
  display: inline-block;
  min-width: 80px;
  text-align: right;
}

.amount-positive {
  color: #059669;
  background-color: #ecfdf5;
}

.amount-negative {
  color: #dc2626;
  background-color: #fef2f2;
}

.amount-zero {
  color: #6b7280;
  background-color: #f9fafb;
}

/* Pagination */
.pagination-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 24px 0;
  flex-wrap: wrap;
  gap: 16px;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 20px;
}

.per-page-selector {
  display: flex;
  align-items: center;
  gap: 8px;
}

.per-page-selector label {
  color: #4b5563;
  font-weight: 500;
  white-space: nowrap;
}

.page-size-select {
  padding: 6px 10px;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  background-color: #fff;
  color: #4b5563;
  cursor: pointer;
  min-width: 70px;
}

.page-info {
  color: #4b5563;
  font-size: 0.9rem;
}

.pagination-nav {
  display: flex;
  align-items: center;
  gap: 12px;
}

.page-status {
  padding: 6px 12px;
  background-color: #f8f9fa;
  border-radius: 6px;
  color: #495057;
  font-weight: 500;
}

.page-btn {
  padding: 6px 12px;
  border: 1px solid #dee2e6;
  background-color: #fff;
  color: #495057;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
}

.page-btn:hover:not(:disabled) {
  background-color: #e9ecef;
  border-color: #ced4da;
}

.page-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.page-btn i {
  font-size: 0.9rem;
}

/* Empty State */
.empty-state {
  padding: 48px 0;
  text-align: center;
  color: #6b7280;
}

.empty-state i {
  font-size: 3.5rem;
  margin-bottom: 16px;
  color: #d1d5db;
}

.empty-state p {
  font-size: 1.1rem;
}

/* Forms and Overlays */
.overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.form-popup {
  background: white;
  padding: 24px;
  border-radius: 10px;
  width: 450px;
  max-width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  z-index: 1001;
  box-shadow: 0 10px 25px rgba(0,0,0,0.15);
  animation: slideUp 0.3s ease;
}

@keyframes slideUp {
  from { transform: translateY(30px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

.form-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.form-header h2 {
  margin: 0;
  color: #212529;
  font-weight: 600;
  font-size: 1.5rem;
}

.btn-close {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1.25rem;
  color: #6b7280;
  padding: 4px;
}

.btn-close:hover {
  color: #1f2937;
}

.form-group {
  margin-bottom: 16px;
}

.form-label {
  display: block;
  margin-bottom: 6px;
  font-weight: 500;
  color: #4b5563;
}

.form-control {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.95rem;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.form-control:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.25);
  outline: none;
}

.form-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 24px;
}

.btn-primary {
  background-color: #3b82f6;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 9px 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary:hover:not(:disabled) {
  background-color: #2563eb;
  box-shadow: 0 2px 5px rgba(37, 99, 235, 0.3);
}

.btn-primary:disabled {
  background-color: #93c5fd;
  cursor: not-allowed;
}

.btn-secondary {
  background-color: white;
  color: #4b5563;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 8px 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-secondary:hover {
  background-color: #f9fafb;
  border-color: #9ca3af;
}

/* User Search and Suggestions */
.search-user-container {
  position: relative;
}

.user-suggestions {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 5;
  background: white;
  border-radius: 6px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  max-height: 250px;
  overflow-y: auto;
  margin-top: 4px;
}

.user-suggestion-item {
  padding: 10px 14px;
  border-bottom: 1px solid #f1f5f9;
  cursor: pointer;
  transition: background 0.15s;
}

.user-suggestion-item:hover {
  background-color: #f8fafc;
}

.user-suggestion-item:last-child {
  border-bottom: none;
}

.user-name {
  display: flex;
  justify-content: space-between;
  font-weight: 500;
  margin-bottom: 2px;
}

.user-role {
  font-size: 0.7rem;
  padding: 2px 6px;
  border-radius: 12px;
  font-weight: 500;
  text-transform: capitalize;
}

.role-student {
  background-color: #e3f2fd;
  color: #0d47a1;
}

.role-teacher {
  background-color: #e8f5e9;
  color: #1b5e20;
}

.role-admin {
  background-color: #fce4ec;
  color: #880e4f;
}

.user-details {
  display: flex;
  justify-content: space-between;
  font-size: 0.85rem;
  color: #64748b;
}

.user-username {
  color: #3b82f6;
}

.user-balance {
  font-family: 'Roboto Mono', monospace;
  font-weight: 500;
}

.amount-input-container {
  position: relative;
}

.amount-hint {
  margin-top: 4px;
  color: #64748b;
  font-size: 0.875rem;
}

/* Toast Notification */
.toast-notification {
  position: fixed;
  bottom: 24px;
  right: 24px;
  padding: 12px 16px;
  background-color: white;
  border-left: 4px solid #10b981;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  display: flex;
  align-items: center;
  min-width: 300px;
  z-index: 1050;
  animation: slideInRight 0.3s, fadeOut 0.3s 2.7s forwards;
}

.toast-notification i {
  font-size: 1.25rem;
  margin-right: 12px;
  color: #10b981;
}

.toast-message {
  font-size: 0.95rem;
  color: #1f2937;
}

@keyframes slideInRight {
  from { transform: translateX(100%); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
}

@keyframes fadeOut {
  from { opacity: 1; }
  to { opacity: 0; }
}

/* Responsive Adjustments */
@media (max-width: 768px) {
  .pagination-container {
    flex-direction: column;
    align-items: center;
  }
  
  .pagination-controls {
    flex-direction: column;
    gap: 12px;
    width: 100%;
  }
  
  .form-popup {
    width: 95%;
  }
}
/* Search with suggestions styling */
.search-input-wrapper {
  position: relative;
  flex: 1;
  width: 100%;
}

.search-suggestions {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  z-index: 100;
  margin-top: 4px;
  max-height: 300px;
  overflow-y: auto;
  max-height: 250px;
}

.suggestion-item {
  padding: 10px 12px;
  line-height: 1.5;
  white-space: normal;
  min-height: 40px;
  display: flex;
  align-items: center;
}

.suggestion-item:last-child {
  border-bottom: none;
}

.suggestion-item:hover {
  background-color: #f8fafc;
}

.suggestion-name {
  font-weight: 500;
  margin-bottom: 2px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.role-tag {
  font-size: 0.7rem;
  padding: 2px 6px;
  border-radius: 12px;
  font-weight: 500;
}

.suggestion-details {
  display: flex;
  justify-content: space-between;
  font-size: 0.85rem;
  color: #64748b;
}

.suggestion-username {
  color: #3b82f6;
}

/* Transaction form user selection */
.search-user-container {
  position: relative;
}

.selected-user {
  margin-top: 8px;
  padding: 10px;
  background-color: #f8fafc;
  border-radius: 6px;
  border-left: 3px solid #3b82f6;
}

.selected-user-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.selected-user-balance {
  font-size: 0.9rem;
  color: #64748b;
}
</style>